import React, { useState } from "react";
import { Button, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { FormField } from "./FormField";
import { useFormStore } from "src/store/dataEntryForm";
import styles from "./styles.module.scss";
import { X } from "lucide-react";

interface Option {
  value: string;
  label: string;
}

interface FieldGroup {
  id: string;
  type: string;
  value?: string;
}

interface FieldGroupWithSelectProps {
  formId: string;
  fieldGroupName: string;
  label: React.ReactNode;
  valueLabel: string;
  options: Option[];
  maxGroups?: number;
  defaultType?: string;
  valueIcon?: React.ReactNode;
  addButtonLabel?: string;
}

export const FieldGroupWithSelect: React.FC<FieldGroupWithSelectProps> = ({
  formId,
  fieldGroupName,
  label,
  valueLabel,
  options,
  maxGroups = 5,
  defaultType,
  valueIcon,
  addButtonLabel,
}) => {
  const { updateFormField, formData } = useFormStore();

  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>(() => {
    const groups: FieldGroup[] = [];
    let idx = 1;
    while (formData[formId]?.[`${fieldGroupName}${idx}Type`] !== undefined) {
      groups.push({
        id: `${fieldGroupName}${idx}`,
        type: formData[formId][`${fieldGroupName}${idx}Type`] as string,
        value:
          (formData[formId][`${fieldGroupName}${idx}Value`] as string) || "",
      });
      idx++;
    }
    if (groups.length === 0) {
      groups.push({
        id: `${fieldGroupName}1`,
        type: defaultType || options[0].value,
      });
    }
    return groups;
  });

  const handleTypeChange = (index: number, newType: string) => {
    const updated = [...fieldGroups];
    updated[index].type = newType;
    setFieldGroups(updated);
    updateFormField(formId, `${fieldGroupName}${index + 1}Type`, newType);
  };

  const addFieldGroup = () => {
    if (fieldGroups.length >= maxGroups) return;
    const newIndex = fieldGroups.length + 1;
    const newGroup: FieldGroup = {
      id: `${fieldGroupName}${newIndex}`,
      type: defaultType || options[0].value,
      value: "",
    };
    setFieldGroups([...fieldGroups, newGroup]);
    updateFormField(formId, `${newGroup.id}Type`, newGroup.type);
    if (valueLabel) {
      updateFormField(formId, `${newGroup.id}Value`, "");
    }
  };

  const removeFieldGroup = (index: number) => {
    const updated = [...fieldGroups];
    const [removed] = updated.splice(index, 1);
    setFieldGroups(updated);
    updateFormField(formId, `${removed.id}Type`, undefined);
    if (valueLabel) {
      updateFormField(formId, `${removed.id}Value`, undefined);
    }
    // переиндексировать оставшиеся
    updated.forEach((grp, i) => {
      const newId = `${fieldGroupName}${i + 1}`;
      const oldType = formData[formId]?.[`${grp.id}Type`];
      const oldValue = formData[formId]?.[`${grp.id}Value`];
      grp.id = newId;
      updateFormField(formId, `${newId}Type`, oldType);
      if (valueLabel) {
        updateFormField(formId, `${newId}Value`, oldValue);
      }
    });
  };

  return (
    <div className={styles.fieldGroupContainer}>
      {fieldGroups.map((group, index) => (
        <div className={styles.row} key={group.id}>
          <div className={styles.selectWrapper}>
            <label className={styles.fieldLabel}>{label}</label>
            <Select
              value={group.type}
              onChange={(value) => handleTypeChange(index, value)}
              className={styles.typeSelect}
              options={options}
              style={{ width: "100%" }}
              dropdownClassName="custom-select-dropdown"
            />
          </div>

          {valueLabel && (
            <FormField
              formId={formId}
              fieldName={`${group.id}Value`}
              label={valueLabel}
              placeholder=""
              type="number"
              size="large"
              icon={valueIcon}
            />
          )}

          {index > 0 && (
            <div className={styles.actionButtonContainer}>
              <Button
                type="text"
                icon={<X color="#7C7C78" />}
                onClick={() => removeFieldGroup(index)}
                className={styles.removeButton}
                danger
              />
            </div>
          )}
        </div>
      ))}

      {fieldGroups.length < maxGroups && (
        <div className={styles.addButtonContainer}>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={addFieldGroup}
            className={styles.addButton}
          >
            ADD ANOTHER {label}
          </Button>
        </div>
      )}
    </div>
  );
};
